import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, LogIn, LayoutDashboard, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "@/lib/site-data";
import { useAuth } from "@/lib/auth";
import { loginUrl } from "@/lib/api";

const NAV_ITEMS = [
    { label: "Accueil", href: "/#accueil" },
    { label: "La pharmacie", href: "/#a-propos" },
    { label: "Équipe", href: "/#equipe" },
    { label: "Services", href: "/#services" },
    { label: "Rendez-vous", href: "/rendez-vous" },
    { label: "Contact", href: "/#contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [adminMenu, setAdminMenu] = useState(false);
    const location = useLocation();
    const { user, logout } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
                scrolled || open ? "ll-nav-scrolled" : ""
            }`}
        >
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Logo imgClass="h-14 w-auto md:h-16" />

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            className="ll-link-underline text-[0.95rem] font-medium text-brand-ink/80 hover:text-brand-primary transition-colors"
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={`tel:${SITE.phone}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-brand-primary"
                        data-testid="nav-phone-link"
                    >
                        <Phone className="h-4 w-4" />
                        {SITE.phoneDisplay}
                    </a>
                    <a href="/rendez-vous" className="ll-btn ll-btn-primary text-sm" data-testid="nav-cta-btn">
                        Prendre RDV
                    </a>
                    {user ? (
                        <div className="relative">
                            <button
                                onClick={() => setAdminMenu(!adminMenu)}
                                onBlur={() => setTimeout(() => setAdminMenu(false), 150)}
                                className="inline-flex items-center gap-2 h-9 pl-1 pr-3 rounded-full border border-brand-line hover:border-brand-primary/60 transition-colors"
                                data-testid="nav-admin-toggle"
                                title={user.name || user.email}
                            >
                                {user.picture ? (
                                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                                ) : (
                                    <span className="h-7 w-7 rounded-full bg-brand-mint text-brand-primary inline-flex items-center justify-center text-xs font-display">
                                        {(user.name || user.email).charAt(0).toUpperCase()}
                                    </span>
                                )}
                            </button>
                            {adminMenu && (
                                <div
                                    className="absolute right-0 top-12 w-56 bg-white border border-brand-line shadow-lg z-50"
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    <Link to="/admin" className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-brand-cream" data-testid="nav-admin-link">
                                        <LayoutDashboard className="h-4 w-4 text-brand-primary" /> Backoffice
                                    </Link>
                                    <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-brand-cream border-t border-brand-line" data-testid="nav-logout-btn">
                                        <LogOut className="h-4 w-4 text-brand-ink/60" /> Déconnexion
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <a
                            href={`${loginUrl}?next=${encodeURIComponent(location.pathname + location.hash)}`}
                            className="inline-flex items-center gap-1.5 text-xs font-sans text-brand-ink/55 hover:text-brand-primary transition-colors"
                            data-testid="nav-team-login-link"
                            title="Accès équipe (Infomaniak)"
                        >
                            <LogIn className="h-3.5 w-3.5" /> Équipe
                        </a>
                    )}
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-line/80 bg-white/60"
                    aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden px-6 pb-6 pt-2 bg-brand-cream/95 border-b border-brand-line">
                    <div className="flex flex-col gap-2">
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.href}
                                href={item.href}
                                className="py-3 text-lg font-display text-brand-ink hover:text-brand-primary border-b border-brand-line/70"
                                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {item.label}
                            </a>
                        ))}
                        <div className="mt-4 flex flex-col gap-3">
                            <a href={`tel:${SITE.phone}`} className="ll-btn ll-btn-ghost justify-center">
                                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
                            </a>
                            <a href="/rendez-vous" className="ll-btn ll-btn-primary justify-center">
                                Prendre RDV
                            </a>
                            {user ? (
                                <Link to="/admin" className="text-xs text-center font-sans text-brand-primary mt-2 inline-flex items-center gap-1.5 justify-center">
                                    <LayoutDashboard className="h-3.5 w-3.5" /> Backoffice
                                </Link>
                            ) : (
                                <a href={`${loginUrl}?next=${encodeURIComponent(location.pathname + location.hash)}`} className="text-xs text-center font-sans text-brand-ink/60 mt-2 inline-flex items-center gap-1.5 justify-center">
                                    <LogIn className="h-3.5 w-3.5" /> Accès équipe
                                </a>
                            )}
                        </div>
                        
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
